import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  bookVisit,
  listMyVisits,
  rescheduleVisit,
  cancelVisit,
  getSlots,
} from "../api/visitApi";
import { listLeads } from "../api/leadApi";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Alert,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Chip,
  Stack,
} from "@mui/material";

const Visits = () => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ lead_id: "", scheduled_time: "" });
  const [slots, setSlots] = useState([]);
  const [leadOptions, setLeadOptions] = useState([]);
  const [startDate, setStartDate] = useState(new Date());

  const fetchVisits = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listMyVisits();
      setVisits(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load visits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisits();
    getSlots()
      .then((res) => setSlots(res.data.slots || []))
      .catch(() => {});
    listLeads()
      .then((res) => setLeadOptions(res.data))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBook = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      await bookVisit({
        lead_id: form.lead_id,
        builder_id: "",
        scheduled_time: form.scheduled_time,
      });
      const selectedLead = leadOptions.find((l) => l.id === form.lead_id);
      setForm({ lead_id: "", scheduled_time: "" });
      setSuccess(
        `Visit booked successfully! WhatsApp confirmation sent to ${
          selectedLead?.name || "the client"
        }.`
      );
      fetchVisits();
      listLeads()
      .then((res) => setLeadOptions(res.data))
      .catch(() => {});
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to book visit");
    }
  };

  const handleReschedule = async (visitId) => {
    const next = prompt("Enter new datetime (ISO, e.g. 2025-01-01T10:00)");
    if (!next) return;
    try {
      await rescheduleVisit(visitId, next);
      fetchVisits();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to reschedule");
    }
  };

  const handleCancel = async (visitId) => {
    if (!window.confirm("Cancel this visit?")) return;
    try {
      await cancelVisit(visitId);
      fetchVisits();
      listLeads()
      .then((res) => setLeadOptions(res.data))
      .catch(() => {});
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "scheduled":
        return "primary";
      case "completed":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
        Site Visits
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Pick from suggested slots or choose your own.
      </Typography>

      {/* Suggested Slots */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          {slots.map((s) => (
            <Chip
              key={s}
              label={dayjs(s).format("MMM D, HH:mm")}
              onClick={() => setForm({ ...form, scheduled_time: s })}
              clickable
              variant="outlined"
            />
          ))}
        </Stack>
      </Box>

      {/* Booking Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Book a Visit
        </Typography>

        <form onSubmit={handleBook}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Lead</InputLabel>
            <Select
              name="lead_id"
              value={form.lead_id}
              onChange={handleChange}
              label="Lead"
              required
            >
              <MenuItem value="">Select lead</MenuItem>
              {leadOptions.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.name} ({l.phone}) — {l.status || "New"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            name="scheduled_time"
            label="Date & Time"
            type="datetime-local"
            value={form.scheduled_time}
            onChange={handleChange}
            required
            margin="normal"
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 2 }}
          />

          <Button type="submit" variant="contained" color="primary" fullWidth>
            Book Visit
          </Button>
        </form>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Visits Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : visits.length > 0 ? (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Lead</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>When</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Map</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visits.map((v) => (
                <TableRow key={v.id} hover>
                  <TableCell>{v.lead_id}</TableCell>
                  <TableCell>
                    {dayjs(v.scheduled_time).format("MMM D, HH:mm")}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={v.status}
                      color={getStatusColor(v.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {v.map_link ? (
                      <Button
                        variant="text"
                        size="small"
                        href={v.map_link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Map
                      </Button>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {/* <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleReschedule(v.id)}
                      >
                        Reschedule
                      </Button> */}
                      {/* <DatePicker>
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="yyyy-MM-dd HH:mm"
                        className="input"
                        onClick={() => handleReschedule(v.id)}
                      Reschedule
                      </DatePicker> */}
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => handleCancel(v.id)}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            No visits booked
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Visits;

